import React, {
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState
} from "react";
import { AgGridReact } from "ag-grid-react";
import { getImagePath } from "../../../utils/getImagePath";
import { Dropdown, Image, Menu, Popconfirm, Spin } from "antd";
import { useTranslation } from "react-i18next";
import {
   CloudDownloadOutlined,
   SearchOutlined,
   EllipsisOutlined
} from "@ant-design/icons";
import useAxios from "axios-hooks";
import "ag-grid-enterprise";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { Resource } from "../../../api/common";
import { MaterialPassport } from "../../../types";
import { useCalculateLca } from "../../../hooks/useCalculateLca";
import { getLcaMeta } from "../../../components/LcaPotential";
import { useHistory, useLocation, useParams } from "react-router";
import { Link } from "react-router-dom";
import { LcaPotential } from "../../../components/LcaPotential";
import filter from "../../../statics/images/filter.svg";
import InputField from "../../../components/FormFields/InputField";
import DropdownField from "../../../components/FormFields/DropdownField";
import ButtonField from "../../../components/FormFields/ButtonField";
import { ScreenSize } from "../../../utils/screenSizes";

export const missingWidth = "Please select a width larger than 0.";
export const missingHeight = "Please select a height larger than 0.";
export const missingDepth = "Please select a depth larger than 0.";

export const MaterialListGrid = ({
   materials,
   refetch
}: {
   materials: MaterialPassport[];
   refetch: () => any;
}) => {
   const [gridApi, setGridApi] = useState<any>(null);
   const [gridColumnApi, setGridColumnApi] = useState(null);
   const [t] = useTranslation();
   const [productGroups, setProductGroups] = useState<any>();
   const [base64Images, setBase64Images] = useState<any>([]);

   const [{ data: pgList, loading }] = useAxios(
      {
         url: `${Resource.path.listProductGroups}`,
         method: "GET"
      },
      { manual: false, useCache: true }
   );
   const history = useHistory();
   const location = useLocation();
   const params: any = useParams();
   const gridRef = useRef();
   let newFilter: any = "category_all";
   const [selectedCategory, setSelectedCategory] = useState<string>("all");
   const [selectedProductGroup, setSelectedProductGroup] = useState<number>(0);
   let selectedCategoryConst: string = selectedCategory;

   useEffect(() => {
      if (pgList?.data) {
         const temp: any = [];
         pgList?.data?.data.map((pg: any) => {
            temp[pg.id] = pg;
         });
         //   console.log(temp);
         setProductGroups(temp);
      }
   }, [pgList]);

   const createBlob = (response: any) => response.blob();

   const fromBlobToBase64 = (blob: any) =>
      new Promise((res) => {
         const reader = new FileReader();
         reader.onloadend = () => res(reader.result);
         reader.readAsDataURL(blob);
      });

   const createBase64ImageFromURL = (url: string) => {
      return fetch(url)
         .then(createBlob)
         .then((blob) => fromBlobToBase64(blob));
   };

   const onGridReady = (params: any) => {
      setGridApi(params.api);
      setGridColumnApi(params.columnApi);
   };

   const getMaterialSpecification = (material: any) => {
      let materialGroupSpecification;

      if (material?.specification_group) {
         materialGroupSpecification = material.specification_group
            .map((item: any, index: number) => {
               if (!item) return null;
               const keys = Object.keys(item);
               return keys.map((key: any, index: number) => {
                  const keyValue = item[key];

                  if (typeof keyValue === "object" && keyValue !== null) {
                     if ("unit" in keyValue && "unitValue" in keyValue) {
                        return `${key}: ${keyValue["unitValue"]}${keyValue["unit"]}`;
                     }
                  } else {
                     // test if color
                     var ddClass;
                     if (/^#([0-9A-F]{3}){1,2}$/i.test(item[key])) {
                        ddClass = "color";
                        return `${key}: ${item[key]}`;
                     } else if (key !== "isRalColorMatched") {
                        return `${key}: ${item[key]}`;
                     }
                  }
               });
            })
            .filter(Boolean);
      }

      return materialGroupSpecification;
   };

   const formattedMaterial = materials?.map((material: any) => {
      const materialDetails = getMaterialSpecification(material);
      const imagePath =
         material?.images && material?.images.length
            ? material?.images[0]
            : material?.default_material_image;

      const imageUrl = getImagePath(imagePath, {
         resize: {
            fit: "cover",
            width: 50
         }
      });
      if (material?.id) {
         createBase64ImageFromURL(imageUrl).then((response: any) => {
            base64Images[material.id] = response;
            setBase64Images(base64Images);
         });
      }
      return {
         ...material,
         imagePath:
            getImagePath(imagePath, {
               resize: {
                  fit: "cover",
                  width: 50
               }
            }) || "error",
         specification_details:
            materialDetails &&
            Array.isArray(materialDetails) &&
            materialDetails.flat().join("\n")
      };
   });

   const colDefsAthleteIncluded = [
      {
         headerName: t("materialDashboard.image"),
         field: "imagePath",
         maxWidth: ScreenSize.mobile ? "100%" : 80,
         cellRenderer: "imageRenderer",
         suppressMenu: true,
         sortable: false,
         minHeight: 110
      },
      {
         headerName: t("materialDashboard.name"),
         field: "product_name",
         filter: "agTextColumnFilter",
         cellRenderer: "detailRendrer",
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: t("materialDashboard.amount"),
         field: "total_amount",
         filter: "agNumberColumnFilter",
         minWidth: 110,
         maxWidth: 130,
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: t("materialDashboard.conditions"),
         field: "clusterDetails",
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: t("materialDashboard.categories"),
         field: "category_name",
         filter: "agSetColumnFilter",
         minWidth: 130,
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: t("materialDashboard.productGroup"),
         field: "product_group_name",
         filter: "agTextColumnFilter",
         minWidth: 150,
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: t("materialDashboard.productUnit"),
         field: "product_unit",
         maxWidth: 100,
         filter: "agSetColumnFilter",
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: t("materialDashboard.manufacturer"),
         field: "manufacturer",
         filter: "agTextColumnFilter",
         minWidth: 170,
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: t("materialDashboard.location"),
         field: "material_current_location",
         filter: "agTextColumnFilter",
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: "Specification Details",
         field: "specification_details",
         minWidth: 180,
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: "LCA",
         field: "product_group_id",
         minWidth: 160,
         // maxWidth: 150,
         cellRenderer: "lcaCellRenderer",
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: "In contact with polluted materials",
         field: "contact_polluted_material_count",
         cellRenderer: "pollutedMaterialCellRenderer",
         minWidth: 150,
         hide: ScreenSize.mobile ? true : false
      },
      {
         wrapText: true,
         autoHeight: true,
         headerName: "Locations",
         field: "roomsAndLocationNote",
         cellRenderer: "roomsAndLocationNote",
         cellStyle: {
            whiteSpace: "normal",
            wordBreak: "normal"
         },
         minWidth: 150,
         hide: ScreenSize.mobile ? true : false
      },
      {
         headerName: "",
         field: "more_button",
         maxWidth: 60,
         pinned: "right",
         cellRenderer: "popupCellRenderer",
         toolPanelClass: "hide"
      }
   ];

   const { calculateTotalLca } = useCalculateLca();

   const onBtExport = () => {
      gridApi.exportDataAsExcel({
         author: "Concular",
         sheetName: "Materials",
         fileName: `${+new Date()}_Materialien`,
         headerRowHeight: 50,
         rowHeight: 100,
         addImageToCell: (rowIndex: any, col: any, value: any) => {
            if (col?.colId === "imagePath" && rowIndex) {
               return value;
            }
         },
         processCellCallback: (params: any) => {
            const column = params?.column?.userProvidedColDef?.headerName;
            if (params?.column?.userProvidedColDef?.field === "imagePath") {
               return {
                  image: {
                     id: params.node.data.id,
                     base64: base64Images[params.node.data.id],
                     imageType: "png",
                     fitCell: true
                  }
               };
            } else if (column === "LCA" && productGroups) {
               try {
                  const passportData = params.node.data;
                  const conversion: any = {
                     width: null,
                     height: null,
                     depth: null,
                     multiplier: null
                  };

                  if (
                     passportData?.specification &&
                     productGroups[params.value]?.templateData &&
                     productGroups[params.value]?.conversion
                  ) {
                     const heightField = productGroups[
                        params.value
                     ].templateData.template
                        .filter((rec: any) => {
                           return (
                              productGroups[params.value]?.conversion?.height
                                 ?.field_id === rec.id
                           );
                        })
                        .pop();
                     if (
                        heightField !== undefined &&
                        passportData.specification[
                           heightField.formItem.name
                        ] !== undefined
                     ) {
                        conversion.height =
                           passportData.specification[
                              heightField.formItem.name
                           ].unitValue;
                     }

                     const widthField = productGroups[
                        params.value
                     ].templateData.template
                        .filter((rec: any) => {
                           return (
                              productGroups[params.value]?.conversion?.width
                                 ?.field_id === rec.id
                           );
                        })
                        .pop();
                     if (
                        widthField !== undefined &&
                        passportData.specification[widthField.formItem.name] !==
                           undefined
                     ) {
                        conversion.width =
                           passportData.specification[
                              widthField.formItem.name
                           ].unitValue;
                     }

                     const depthField = productGroups[
                        params.value
                     ].templateData.template
                        .filter((rec: any) => {
                           return (
                              productGroups[params.value]?.conversion?.depth
                                 ?.field_id === rec.id
                           );
                        })
                        .pop();
                     if (
                        depthField !== undefined &&
                        passportData.specification[depthField.formItem.name] !==
                           undefined
                     ) {
                        conversion.depth =
                           passportData.specification[
                              depthField.formItem.name
                           ].unitValue;
                     }
                  }
                  conversion.multiplier =
                     productGroups[params.value].conversion?.multiplier;
                  const material = getLcaMeta(
                     productGroups[params.value],
                     passportData?.selected_material?.sub_material_id
                  );
                  const lcaValue = calculateTotalLca(
                     productGroups[params.value].unit,
                     material?.lca_unit || productGroups[params.value].lca_unit,
                     productGroups[params.value]?.lca_meta &&
                        productGroups[params.value]?.lca_meta.length > 0
                        ? material
                        : productGroups[params.value],
                     passportData.total_amount,
                     conversion
                  );

                  return lcaValue.status === "ok" && lcaValue.result
                     ? `${lcaValue.result.toFixed(2)} kgCO2-eq`
                     : ``;
               } catch (error) {
                  return error;
               }
            } else if (column === "In contact with polluted materials") {
               return params.value &&
                  params.value > 0 &&
                  productGroups[params?.node?.data?.product_group_id] &&
                  productGroups[params?.node?.data?.product_group_id]?.unit
                  ? `Yes  (${params.value} ${
                       productGroups[params?.node?.data?.product_group_id].unit
                    })`
                  : ``;
            } else {
               return params.value;
            }
         }
      });
   };

   const LCA = (data: any) => {
      const pgData = productGroups[data.value];
      /*  return (
         <span>
            {pgData && pgData?.obdProductUuid
               ? `${
                    +!isNaN(pgData?.gwp_a1a3 + pgData?.gwp_c4) &&
                    (pgData?.gwp_a1a3 || 0 + pgData?.gwp_c4 || 0)?.toFixed(2)
                 } ${pgData?.lca_unit}`
               : ""}
         </span>
      ); */
      return (
         <LcaPotential
            hideHtml
            amount={data?.data?.total_amount}
            productGroup={pgData}
            selectedMaterial={data?.data?.selected_material?.sub_material_id}
            passport={data?.data}
         />
      );
   };

   const showDetail = (rec: any) => {
      const { value, data }: any = rec;
      return (
         <Link
            className="link-item"
            to={{ pathname: `${location.pathname}/${data.id}` }}
            target="_blank"
         >
            {value}
         </Link>
      );
   };

   const handleDuplicate = async (id: number) => {
      await executeGet({
         url: `${Resource.path.cloneMaterialSpec}/${id}`
      }).then((response) => {
         if (response?.data?.data) {
            history.push({
               pathname: `${location.pathname}/edit/${response?.data?.data}/product_details`
            });
         }
      });
   };

   const handleDelete = async (id: number) => {
      await executeDelete({
         url: `${Resource.path.removeMaterialPassport}/${id}`
      });
      refetch();
   };

   const reqestItemMenu = (id: number) => (
      <Menu>
         <Menu.Item
            onClick={() =>
               window.open(
                  `/buildings/${params?.id}/material_inventory/edit/${id}/product_details`,
                  "_blank"
               )
            }
         >
            {t("default.edit")}
         </Menu.Item>
         <Menu.Item onClick={() => handleDuplicate(id)}>
            {t("default.duplicate")}
         </Menu.Item>
         <Menu.Item>
            <Popconfirm
               title={t("settings.organization.areYouSure")}
               onConfirm={() => handleDelete(id)}
               icon={null}
               okText={t("default.yes")}
               cancelText={t("default.no")}
               overlayClassName={"confirm-no-icon"}
               okButtonProps={{
                  loading: deleteOperationLoading
               }}
            >
               {t("default.delete")}
            </Popconfirm>
         </Menu.Item>
      </Menu>
   );

   const ShowRowButton = (rec: any) => {
      const { data } = rec;
      return (
         <Dropdown overlay={reqestItemMenu(data.id)}>
            <a
               className="ant-dropdown-link dotted-menu"
               onClick={(e) => e.preventDefault()}
               style={{
                  padding: "10px",
                  zIndex: 10,
                  color: "#000",
                  display: "flex",
                  justifyContent: "center"
               }}
            >
               <EllipsisOutlined className="icon" />
            </a>
         </Dropdown>
      );
   };

   const GetPollutedMaterial = (rec: any) => {
      const { value, data }: any = rec;
      const pgData = productGroups[data.product_group_id];

      return (
         <span>
            {value && value > 0 && pgData && pgData?.unit
               ? `Yes  (${value} ${pgData.unit})`
               : ``}
         </span>
      );
   };

   const onFilterTextBoxChanged = useCallback(() => {
      gridRef?.current?.api?.setQuickFilter(
         document.getElementById("filter-text-box").value
      );
   }, []);

   const handleChangeFilter = useCallback(
      (id: any, value?: string, key?: string) => {
         newFilter = `${key}_${value}`;
         if (key === "category" && value) {
            selectedCategoryConst = value;
            setSelectedCategory(value);
            setSelectedProductGroup(0);
         } else {
            setSelectedProductGroup(id);
         }
         gridRef?.current?.api?.onFilterChanged();
      },
      []
   );

   const isExternalFilterPresent = useCallback(() => {
      return newFilter !== "allCategories" || newFilter !== "allProductGroups";
   }, [newFilter]);

   const doesExternalFilterPass = useCallback(
      (node) => {
         const filter = newFilter.split("_");
         switch (filter[0]) {
            case "category":
               return filter[1] == "all"
                  ? true
                  : node.data.category_name == filter[1];
            case "productGroup":
               return filter[1] == "all"
                  ? selectedCategoryConst === "all"
                     ? true
                     : node.data.category_name == selectedCategoryConst
                  : node.data.product_group.label == filter[1];
            default:
               return true;
         }
      },
      [newFilter]
   );

   const sideBar = useMemo(() => {
      return {
         toolPanels: [
            {
               id: "columns",
               labelDefault: "",
               labelKey: "columns",
               iconKey: "columns",
               toolPanel: "agColumnsToolPanel",
               toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true,
                  suppressColumnFilter: true,
                  suppressColumnSelectAll: true,
                  suppressColumnExpandAll: true
               }
            }
         ]
      };
   }, []);

   const icons = useMemo(() => {
      return {
         menu: `<img src=${filter} style="height: 18px; width: 18px; padding-right: 2px"/>`
      };
   }, []);

   const [
      {
         data: deleteOperationData,
         loading: deleteOperationLoading,
         error: deleteOperationError
      },
      executeDelete
   ] = useAxios(
      {
         method: "DELETE"
      },
      { manual: true }
   );

   const [{ data: _data, loading: _loading, error: _error }, executeGet] =
      useAxios(
         {
            method: "GET"
         },
         { manual: true }
      );

   const GetImage = ({ value, data }: any) => {
      return (
         <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Link
               className="link-item"
               to={{ pathname: `${location.pathname}/${data.id}` }}
               target="_blank"
            >
               {ScreenSize.mobile ? (
                  <div className="d-flex">
                     <Image
                        width={50}
                        src={value}
                        style={{
                           objectFit: "cover",
                           objectPosition: "50% 50%",
                           maxHeight: "41px"
                        }}
                        preview={false}
                        fallback="data:image/png;base64,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"
                     />
                     <div className={"product-info-mobile"}>
                        <span>{data.product_name}</span>
                        <span>{`${data.total_amount || ""} ${
                           data.total_amount
                              ? t("materialDashboard.pieces")
                              : ""
                        }`}</span>
                     </div>
                  </div>
               ) : (
                  <Image
                     width={50}
                     src={value}
                     style={{
                        objectFit: "cover",
                        objectPosition: "50% 50%",
                        maxHeight: "41px"
                     }}
                     preview={false}
                     fallback="data:image/png;base64,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"
                  />
               )}
            </Link>
         </div>
      );
   };

   const getRowClass = (params: any) => {
      return ScreenSize.mobile ? "custom-row-styles" : "";
   };

   const getRowHeight = (params: any) => (ScreenSize.mobile ? 70 : false);

   const getCategories = () => {
      let categories = [
         {
            id: 0,
            name: "All categories",
            value: "all"
         }
      ];
      if (materials.length) {
         const all = materials.map((item) => item.category_name);
         const removedDuplicates = Array.from(new Set(all));
         categories = [
            {
               id: 0,
               name: "All categories",
               value: "all"
            },
            ...removedDuplicates.map((item: any, index: number) => ({
               id: index + 1,
               name: item,
               value: item
            }))
         ];
      }
      return categories;
   };

   const getProductGroups = () => {
      let productGroups = [
         {
            id: 0,
            name: "All product groups",
            value: "all"
         }
      ];
      if (materials.length) {
         const all = materials
            .filter((item: any) =>
               selectedCategory == "all"
                  ? item
                  : item.category_name == selectedCategory
            )
            .map((item: any) => item?.product_group?.label);
         const removedDuplicates = Array.from(new Set(all));
         productGroups = [
            {
               id: 0,
               name: "All product groups",
               value: "all"
            },
            ...removedDuplicates.map((item: any, index: number) => ({
               id: index + 1,
               name: item,
               value: item
            }))
         ];
      }
      return productGroups;
   };

   return (
      <div className="content-wrap grid-view">
         <Spin spinning={loading}>
            <div className="material-overview-wrapper">
               <div className="head-elements">
                  <div className={"externalFilters"}>
                     <InputField
                        id="filter-text-box"
                        onInput={onFilterTextBoxChanged}
                        formItem={false}
                        label={
                           ScreenSize.mobile
                              ? ""
                              : t("materialDashboard.searchProduct")
                        }
                        placeholder={
                           ScreenSize.mobile
                              ? t("materialDashboard.searchProduct")
                              : ""
                        }
                        icon={<SearchOutlined />}
                        className="search-product"
                     />
                     <DropdownField
                        id="filter-text-box"
                        label={t("materialDashboard.filterByCategory")}
                        selected={{ id: 0 }}
                        items={getCategories()}
                        onChange={(id, value) =>
                           handleChangeFilter(id, value, "category")
                        }
                        className={"categoryFilter"}
                     />
                     <DropdownField
                        id="filter-text-box"
                        label={t("materialDashboard.filterByProductGroups")}
                        selected={{
                           id: selectedProductGroup
                        }}
                        formItem={false}
                        items={getProductGroups()}
                        onChange={(id, value) =>
                           handleChangeFilter(id, value, "productGroup")
                        }
                        className={"productGroupFilter"}
                     />
                  </div>

                  <ButtonField
                     onClick={() => onBtExport()}
                     style={{ marginBottom: "5px", fontWeight: "bold" }}
                     type="alternative"
                     className="export-excel"
                  >
                     <CloudDownloadOutlined />
                     {t("materialDashboard.exportToExcell")}
                  </ButtonField>
               </div>
               {productGroups && (
                  <div
                     className="ag-theme-alpine inventory"
                     style={{
                        height: 600
                        // paddingLeft: "20px",
                        // paddingRight: "20px"
                     }}
                  >
                     <AgGridReact
                        ref={gridRef}
                        onGridReady={onGridReady}
                        rowData={formattedMaterial}
                        columnDefs={colDefsAthleteIncluded}
                        getRowHeight={getRowHeight}
                        defaultColDef={{
                           flex: 1,
                           minWidth: 130,
                           sortable: true,
                           resizable: true,
                           filter: true,
                           menuTabs: ["filterMenuTab"]
                        }}
                        enableMenu={ScreenSize.mobile ? false : true}
                        animateRows={true}
                        headerHeight={ScreenSize.mobile ? 0 : true}
                        isExternalFilterPresent={isExternalFilterPresent}
                        doesExternalFilterPass={doesExternalFilterPass}
                        sideBar={ScreenSize.mobile ? false : sideBar}
                        icons={icons}
                        getRowClass={getRowClass}
                        frameworkComponents={{
                           lcaCellRenderer: LCA,
                           pollutedMaterialCellRenderer: GetPollutedMaterial,
                           detailRendrer: showDetail,
                           imageRenderer: GetImage,
                           popupCellRenderer: ShowRowButton
                        }}
                     />
                  </div>
               )}
            </div>
         </Spin>
      </div>
   );
};
